import { createContext, type ReactNode, use, useCallback, useMemo } from "react";
import { fireConfetti, preloadConfetti } from "@/features/kudos/celebration/confetti";
import { useReducedMotion } from "@/shared/hooks/use-reduced-motion";

export type CelebrateKind = "give" | "clap" | "arrive";

interface CelebrateOptions {
  /// Anchor element to use as the burst origin (e.g. the clap button rect).
  /// Falls back to a center-top burst when omitted.
  anchor?: Element | null;
}

interface KudosCelebrationContextValue {
  celebrate: (kind: CelebrateKind, opts?: CelebrateOptions) => void;
  preload: () => void;
  reducedMotion: boolean;
}

const KudosCelebrationContext = createContext<KudosCelebrationContextValue | null>(null);

interface Props {
  children: ReactNode;
}

/// Owns the kudos celebration confetti. Mount once at the route level (kudos
/// wall + kudos detail). Descendants opt in by calling `useKudosCelebration()`.
/// The hook returns `null` outside the provider so shared components
/// (e.g. `<ClapButton>` on the relationship board) stay silent there.
export function KudosCelebrationProvider({ children }: Props): ReactNode {
  const reducedMotion = useReducedMotion();

  const celebrate = useCallback(
    (kind: CelebrateKind, opts: CelebrateOptions = {}) => {
      if (reducedMotion) return;
      const origin = anchorOrigin(opts.anchor) ?? { x: 0.5, y: 0.35 };

      if (kind === "clap") {
        void fireConfetti({ origin, intensity: "small" });
        return;
      }

      // "give" and "arrive" both get the big shower.
      void fireConfetti({ origin, intensity: "big" });
    },
    [reducedMotion],
  );

  const value = useMemo<KudosCelebrationContextValue>(
    () => ({
      celebrate,
      preload: preloadConfetti,
      reducedMotion,
    }),
    [celebrate, reducedMotion],
  );

  return <KudosCelebrationContext value={value}>{children}</KudosCelebrationContext>;
}

/// Read the celebration context. Returns `null` outside `<KudosCelebrationProvider>`
/// so shared components can call it harmlessly from non-kudos surfaces.
export function useKudosCelebration(): KudosCelebrationContextValue | null {
  return use(KudosCelebrationContext);
}

function anchorOrigin(anchor: Element | null | undefined): { x: number; y: number } | null {
  if (!anchor || typeof window === "undefined") return null;
  const rect = anchor.getBoundingClientRect();
  const w = window.innerWidth || 1;
  const h = window.innerHeight || 1;
  return {
    x: Math.max(0, Math.min(1, (rect.left + rect.width / 2) / w)),
    y: Math.max(0, Math.min(1, (rect.top + rect.height / 2) / h)),
  };
}
